import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Select } from 'antd';
import VirtualList, { type ListRef } from 'rc-virtual-list';

import { SEARCH_RESULTS } from '@/shared/lib/constants';
import type { SortByOption } from '@/shared/types/options';
import { LoadingState } from '@/shared/ui/states/LoadingState';

import { useDomainHierarchy } from '@/entities/domain';
import type { DomainHierarchyItem } from '@/entities/domain';
import { useSearchStore } from '@/entities/search';
import type { SearchResult } from '@/entities/search';
import { useAvailableTags } from '@/entities/tags';

import { getUniqueKey } from '@/features/search/model/_internal/cacheManager';

import { SearchResultItem } from './SearchResultItem';
import styles from './SearchResults.module.css';

type SearchResultsProps = {
  results: SearchResult[];
  query: string;
  totalCount: number;
  isFetching?: boolean;
  isLoading?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  selectedId?: string | null;
  onSelectResult?: (result: SearchResult) => void;
  previewPanel?: React.ReactNode;
  style?: React.CSSProperties;
  headerRight?: React.ReactNode;
  showResultCount?: boolean;
  emptyContent?: React.ReactNode;
  listResetKey?: string;
};

export const SearchResults: React.FC<SearchResultsProps> = ({
  results,
  query,
  totalCount,
  isFetching,
  isLoading,
  onLoadMore,
  hasMore,
  selectedId,
  onSelectResult,
  previewPanel,
  style = {},
  headerRight,
  showResultCount = true,
  emptyContent,
  listResetKey,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const virtualListRef = useRef<ListRef>(null);
  const scrollHolderRef = useRef<HTMLElement | null>(null);
  const [containerHeight, setContainerHeight] = useState(0);
  const { data: availableTags = [] } = useAvailableTags();
  const { data: domainHierarchyData } = useDomainHierarchy();

  const domainDisplayNameByFqn = useMemo(() => {
    const map = new Map<string, string>();

    const walk = (items: DomainHierarchyItem[]) => {
      items.forEach((item) => {
        if (item.fullyQualifiedName) {
          map.set(item.fullyQualifiedName, item.displayName);
        }
        if (item.children.length > 0) walk(item.children);
      });
    };

    if (domainHierarchyData?.data) {
      walk(domainHierarchyData.data);
    }

    return map;
  }, [domainHierarchyData?.data]);

  const tagLabelByFqn = useMemo(() => {
    return new Map(availableTags.map((tag) => [tag.value, tag.label] as const));
  }, [availableTags]);

  useEffect(() => {
    const updateHeight = () => {
      if (containerRef.current) {
        const height = containerRef.current.clientHeight;
        setContainerHeight(height > 0 ? height - SEARCH_RESULTS.CONTAINER_OFFSET : 0);
      }
    };

    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    window.addEventListener('resize', updateHeight);

    return () => {
      window.removeEventListener('resize', updateHeight);
      resizeObserver.disconnect();
    };
  }, []);

  const { sortBy, setSortBy } = useSearchStore();
  const sortOptions = useMemo(
    () =>
      [
        { label: '최신순', value: 'lastUpdated' as const },
        { label: '가장 오래된 순', value: 'lastUpdatedAsc' as const },
        { label: '이름순', value: 'name' as const },
      ] satisfies ReadonlyArray<{ label: string; value: SortByOption }>,
    []
  );

  useEffect(() => {
    const allowed = new Set<SortByOption>(sortOptions.map((o) => o.value));
    if (!allowed.has(sortBy)) {
      setSortBy('lastUpdated');
    }
  }, [sortBy, setSortBy, sortOptions]);

  useEffect(() => {
    if (virtualListRef.current) {
      virtualListRef.current.scrollTo(0);
    }
    scrollHolderRef.current = null;
  }, [sortBy, listResetKey]);

  const getScrollHolder = useCallback((): HTMLElement | null => {
    const fromRef = scrollHolderRef.current;
    if (fromRef?.isConnected) return fromRef;
    const root = virtualListRef.current?.nativeElement;
    const holder = root?.querySelector?.('.rc-virtual-list-holder');
    return holder instanceof HTMLElement ? holder : null;
  }, []);

  const tryLoadMoreIfNearBottom = useCallback(() => {
    if (!onLoadMore || !hasMore || isLoading || isFetching) return;
    const el = getScrollHolder();
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    if (distanceFromBottom <= SEARCH_RESULTS.SCROLL_THRESHOLD + 1) {
      onLoadMore();
    }
  }, [getScrollHolder, onLoadMore, hasMore, isLoading, isFetching]);

  useEffect(() => {
    if (isLoading || isFetching || !hasMore || !onLoadMore) return;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => tryLoadMoreIfNearBottom());
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [isFetching, isLoading, hasMore, onLoadMore, results.length, listResetKey, tryLoadMoreIfNearBottom]);

  return (
    <div className={styles.resultWrapper} style={style}>
      <div className={styles.resultContainer}>
        <div className={styles.resultHeader}>
          <Select
            value={sortBy}
            onChange={(value) => setSortBy(value)}
            options={sortOptions}
            className={styles.sortSelect}
          />
          {headerRight}
        </div>
        <div className={`${styles.resultList} ${selectedId ? styles.hasPreview : ''}`} ref={containerRef}>
          {isLoading ? (
            <div className={styles.emptyContentWrapper}>
              <LoadingState
                message="검색 결과를 불러오는 중..."
                style={{
                  backgroundColor: 'var(--opds-glo-palette-gray-98)',
                }}
              />
            </div>
          ) : results.length === 0 ? (
            emptyContent ? (
              <div className={styles.emptyContentWrapper}>{emptyContent}</div>
            ) : null
          ) : containerHeight > 0 ? (
            <>
              <VirtualList
                key={listResetKey ?? 'search-results-list'}
                ref={virtualListRef}
                data={results}
                height={containerHeight}
                itemKey={(result: SearchResult) => getUniqueKey(result)}
                onScroll={(e: React.UIEvent<HTMLElement>) => {
                  const target = e.currentTarget;
                  scrollHolderRef.current = target;
                  const distanceFromBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
                  if (
                    distanceFromBottom <= SEARCH_RESULTS.SCROLL_THRESHOLD + 1 &&
                    onLoadMore &&
                    hasMore &&
                    !isLoading &&
                    !isFetching
                  ) {
                    onLoadMore();
                  }
                }}>
                {(result: SearchResult) => (
                  <SearchResultItem
                    key={getUniqueKey(result)}
                    result={result}
                    query={query}
                    isSelected={selectedId === getUniqueKey(result)}
                    onSelectResult={onSelectResult}
                    domainDisplayNameByFqn={domainDisplayNameByFqn}
                    tagLabelByFqn={tagLabelByFqn}
                  />
                )}
              </VirtualList>
              {isFetching && !isLoading && hasMore && (
                <div className={styles.loadingMore}>
                  <LoadingState message="추가 결과를 불러오는 중..." size={32} />
                </div>
              )}
            </>
          ) : null}
        </div>
        {showResultCount && (
          <div className={styles.resultCount}>
            총 <span>{totalCount.toLocaleString()}</span>개의 결과
          </div>
        )}
      </div>
      {previewPanel && selectedId && <div className={styles.previewContainer}>{previewPanel}</div>}
    </div>
  );
};
